import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Agenda do professor no formato do calendário do celular: a fita da semana
 * em cima, a linha do tempo do dia embaixo e um traço com a hora de agora.
 *
 * O professor que dá aula em várias turmas (e às vezes em mais de uma
 * escola) abre o app no corredor e precisa de uma resposta só: "onde eu
 * estou agora e para onde vou depois". Por isso os dois cartões no topo —
 * Agora e Próxima — e a grade do dia inteiro logo abaixo, de todas as turmas
 * juntas.
 *
 * Componente só de apresentação: recebe os slots da grade horária
 * (`/class-schedule/`) e a hora de referência. Quem busca é o AgendaProfessor.
 */

const DIAS_CURTOS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const DIAS_LONGOS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const MESES = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];

/** Altura de uma hora na linha do tempo, em px. */
const HORA_PX = 72;

/** Mesma paleta da Grade Horária: a disciplina mantém a cor nas duas telas. */
const CORES = ['#0974BA', '#13983D', '#F9B047', '#E83A3C', '#7C3AED', '#0891B2', '#DB2777', '#65A30D'];
const corDaDisciplina = (idSubject) => (idSubject ? CORES[Number(idSubject) % CORES.length] : '#94A3B8');

const AGORA_COR = '#E83A3C';

/** "07:30:00" → 450 (minutos desde a meia-noite). */
export const paraMinutos = (hora) => {
  if (!hora) return null;
  const [h, m] = String(hora).split(':').map(Number);
  return h * 60 + (m || 0);
};

const hhmm = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

const isoDoDia = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const mesmoDia = (a, b) => isoDoDia(a) === isoDoDia(b);

const somarDias = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

/** A grade usa 0 = segunda; o Date usa 0 = domingo. */
const weekdayDaGrade = (d) => (d.getDay() + 6) % 7;

/** "em 1 h 05 min" / "em 12 min". */
const duracao = (min) => {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${String(m).padStart(2, '0')} min` : `${h} h`;
};

/**
 * Aulas de uma data: o dia da semana bate e a data cai na vigência do slot.
 * Ordenadas pelo horário; sem horário cadastrado, pelo número da aula.
 */
export function aulasDoDia(slots, data) {
  const iso = isoDoDia(data);
  const wd = weekdayDaGrade(data);
  return slots
    .filter((s) => s.is_active !== false && Number(s.weekday) === wd)
    .filter((s) => {
      const inicio = s.effective_from || s.valid_from;
      return (!inicio || inicio <= iso) && (!s.valid_until || s.valid_until >= iso);
    })
    .map((s) => ({ ...s, ini: paraMinutos(s.start_time), fim: paraMinutos(s.end_time) }))
    .sort((a, b) => (a.ini ?? a.period * 60) - (b.ini ?? b.period * 60));
}

/**
 * A aula em andamento e a seguinte. A seguinte pode ser de outro dia: depois
 * da última aula de sexta, a próxima é a primeira de segunda.
 */
export function agoraEProxima(slots, agora) {
  const minAgora = agora.getHours() * 60 + agora.getMinutes();
  const hoje = aulasDoDia(slots, agora).filter((a) => a.ini != null && a.fim != null);
  const atual = hoje.find((a) => a.ini <= minAgora && minAgora < a.fim) || null;
  let proxima = hoje.find((a) => a.ini > minAgora);
  if (proxima) return { atual, proxima: { ...proxima, data: agora } };
  for (let i = 1; i <= 7; i += 1) {
    const dia = somarDias(agora, i);
    const primeira = aulasDoDia(slots, dia).find((a) => a.ini != null);
    if (primeira) return { atual, proxima: { ...primeira, data: dia } };
  }
  return { atual, proxima: null };
}

function CartaoAula({ rotulo, aula, detalhe, progresso, destaque, darkMode, onAbrir }) {
  const strong = darkMode ? 'text-slate-100' : 'text-slate-800';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const base = darkMode ? 'bg-slate-800/60 border-slate-700/60' : 'bg-white border-slate-200 shadow-sm';

  if (!aula) {
    return (
      <div className={`rounded-2xl border p-4 ${base}`}>
        <div className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>{rotulo}</div>
        <div className={`mt-2 text-base font-medium ${strong}`}>{detalhe}</div>
      </div>
    );
  }

  const cor = corDaDisciplina(aula.id_subject);
  return (
    <button
      type="button"
      onClick={() => onAbrir?.(aula)}
      className={`text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5 ${base}`}
      style={destaque ? { borderColor: AGORA_COR, boxShadow: `0 0 0 1px ${AGORA_COR}` } : undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={`text-[11px] font-semibold uppercase tracking-wide ${destaque ? '' : muted}`}
          style={destaque ? { color: AGORA_COR } : undefined}
        >
          {rotulo}
        </span>
        <span className={`text-xs tabular-nums ${muted}`}>
          {aula.ini != null ? `${hhmm(aula.ini)} – ${hhmm(aula.fim)}` : `${aula.period}ª aula`}
        </span>
      </div>
      <div className="mt-2 flex items-start gap-3">
        <span className="mt-1 w-1.5 self-stretch rounded-full flex-none" style={{ background: cor }} />
        <div className="min-w-0">
          <div className={`text-lg font-semibold leading-tight ${strong}`}>{aula.class_name}</div>
          <div className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{aula.subject_name}</div>
          <div className={`text-xs mt-0.5 truncate ${muted}`}>{aula.school_name}</div>
        </div>
      </div>
      {progresso != null && (
        <div className={`mt-3 h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
          <div className="h-full rounded-full" style={{ width: `${progresso}%`, background: AGORA_COR }} />
        </div>
      )}
      <div className={`mt-2 text-xs font-medium ${destaque ? '' : muted}`} style={destaque ? { color: AGORA_COR } : undefined}>
        {detalhe}
      </div>
    </button>
  );
}

/**
 * `titulo` abre a coluna da agenda. `ladoDesktop`, quando vem, ocupa no
 * desktop o lugar da semana e da linha do tempo (o Meu Painel põe ali os
 * números da turma); no celular a linha do tempo continua, e quem chamou
 * mostra `ladoDesktop` no lugar de sempre.
 */
export default function AgendaDoDia({ slots = [], agora: agoraProp, darkMode = false, onAbrirAula, titulo, ladoDesktop }) {
  // Sem `agora` vindo de fora, o relógio anda sozinho a cada 30 s: a tela
  // fica aberta no celular durante a aula e a linha precisa acompanhar.
  const [relogio, setRelogio] = useState(() => new Date());
  useEffect(() => {
    if (agoraProp) return undefined;
    const t = setInterval(() => setRelogio(new Date()), 30000);
    return () => clearInterval(t);
  }, [agoraProp]);
  const agora = agoraProp || relogio;

  const [diaSel, setDiaSel] = useState(agora);
  // No celular a linha do tempo começa fechada: aberta, ocupava quase três
  // telas e empurrava as pendências da turma para longe. Agora e Próxima
  // respondem o que o professor procura na correria; o dia todo fica a um
  // toque. No desktop ela não depende disso.
  const [diaAberto, setDiaAberto] = useState(false);
  const ehHoje = mesmoDia(diaSel, agora);

  const domingo = somarDias(agora, -agora.getDay());
  const semana = Array.from({ length: 7 }, (_, i) => somarDias(domingo, i));

  const aulas = useMemo(() => aulasDoDia(slots, diaSel), [slots, diaSel]);
  const comHorario = aulas.filter((a) => a.ini != null && a.fim != null);
  const semHorario = aulas.filter((a) => a.ini == null || a.fim == null);
  const diaSeguinte = somarDias(diaSel, 1);
  const amanha = useMemo(() => aulasDoDia(slots, diaSeguinte), [slots, diaSeguinte.toDateString()]); // eslint-disable-line react-hooks/exhaustive-deps
  const { atual, proxima } = useMemo(() => agoraEProxima(slots, agora), [slots, agora]);

  const minAgora = agora.getHours() * 60 + agora.getMinutes();
  const aulasDeHoje = useMemo(() => aulasDoDia(slots, agora).length, [slots, agora.toDateString()]); // eslint-disable-line react-hooks/exhaustive-deps

  // A linha do tempo vai da primeira à última aula da semana, com folga de
  // uma hora: o turno não pula de tamanho quando o professor troca de dia.
  const todos = slots.map((s) => [paraMinutos(s.start_time), paraMinutos(s.end_time)]).filter(([i, f]) => i != null && f != null);
  const horaIni = todos.length ? Math.max(0, Math.floor(Math.min(...todos.map(([i]) => i)) / 60) - 1) : 7;
  const horaFim = todos.length ? Math.min(24, Math.ceil(Math.max(...todos.map(([, f]) => f)) / 60) + 1) : 18;
  const horas = Array.from({ length: horaFim - horaIni + 1 }, (_, i) => horaIni + i);
  const topo = (min) => ((min - horaIni * 60) / 60) * HORA_PX;

  // Abre já rolado para a hora atual, como o calendário do celular.
  const rolagem = useRef(null);
  useEffect(() => {
    if (!rolagem.current) return;
    const alvo = ehHoje ? topo(minAgora) - 120 : comHorario.length ? topo(comHorario[0].ini) - 40 : 0;
    rolagem.current.scrollTop = Math.max(0, alvo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diaSel, diaAberto]);

  const strong = darkMode ? 'text-slate-100' : 'text-slate-800';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const linha = darkMode ? 'border-slate-700/70' : 'border-slate-200';
  const card = darkMode ? 'bg-slate-800/50 border border-slate-700/40' : 'bg-white border border-slate-200 shadow-sm';

  const quandoProxima = () => {
    if (!proxima) return 'Nenhuma aula na grade';
    if (mesmoDia(proxima.data, agora)) return `Começa em ${duracao(proxima.ini - minAgora)}`;
    if (mesmoDia(proxima.data, somarDias(agora, 1))) return `Amanhã às ${hhmm(proxima.ini)}`;
    return `${DIAS_LONGOS[proxima.data.getDay()]} às ${hhmm(proxima.ini)}`;
  };

  // O dia seguinte já aparece: o professor confere o que leva amanhã.
  // Aulas de hoje para o desktop: sem relógio. No computador o professor
  // lança a chamada antes ou depois da aula, então "agora" e "próxima" não
  // ajudam; o que ajuda é chegar à chamada de qualquer aula do dia.
  const doDiaDeHoje = useMemo(() => aulasDoDia(slots, agora), [slots, agora.toDateString()]); // eslint-disable-line react-hooks/exhaustive-deps
  const listaHoje = (
    <div className="hidden lg:flex lg:flex-col min-w-0">
      <h2 className={`text-sm font-semibold mb-3 ${strong}`}>
        Aulas de hoje{' '}
        <span className={`font-normal ${muted}`}>
          · {DIAS_LONGOS[agora.getDay()].toLowerCase()}, {agora.getDate()} de {MESES[agora.getMonth()]}
        </span>
      </h2>
      <div className={`flex-1 rounded-2xl overflow-hidden ${card}`}>
        {doDiaDeHoje.length === 0 && (
          <div className={`p-6 text-sm ${muted}`}>Nenhuma aula hoje na sua grade.</div>
        )}
        <div className={`divide-y ${darkMode ? 'divide-slate-700/60' : 'divide-slate-100'}`}>
          {doDiaDeHoje.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onAbrirAula?.(a, agora)}
              title="Abrir a chamada desta aula"
              className={`group w-full flex items-center gap-4 px-4 py-3 text-left text-sm transition-colors ${
                darkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}`}
            >
              <span
                className="w-10 h-10 flex-none grid place-items-center rounded-lg text-xs font-semibold text-white"
                style={{ background: corDaDisciplina(a.id_subject) }}
              >
                {a.period}ª
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block font-semibold ${strong}`}>
                  {a.class_name} <span className={`font-normal ${muted}`}>· {a.subject_name}</span>
                </span>
                <span className={`block text-xs truncate ${muted}`}>
                  {a.ini != null ? `${hhmm(a.ini)} – ${hhmm(a.fim)} · ` : ''}{a.school_name}
                </span>
              </span>
              <span className="flex-none text-xs font-medium text-[#0974BA] opacity-70 group-hover:opacity-100">
                Chamada ›
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const listaAmanha = (cls) => amanha.length > 0 && (
    <div className={`px-4 py-3 ${cls}`}>
      <div className={`text-[11px] font-semibold uppercase tracking-wide mb-2 ${muted}`}>
        {DIAS_LONGOS[diaSeguinte.getDay()]}, {diaSeguinte.getDate()} · {amanha.length} {amanha.length === 1 ? 'aula' : 'aulas'}
      </div>
      <div className="flex flex-col gap-1.5">
        {amanha.map((a) => (
          <button key={a.id} type="button" onClick={() => onAbrirAula?.(a, diaSeguinte)} className="flex items-center gap-3 text-left text-sm">
            <span className={`w-11 flex-none text-xs tabular-nums ${muted}`}>{a.ini != null ? hhmm(a.ini) : `${a.period}ª`}</span>
            <span className="w-2 h-2 rounded-full flex-none" style={{ background: corDaDisciplina(a.id_subject) }} />
            <span className={`font-medium whitespace-nowrap ${strong}`}>{a.class_name}</span>
            <span className={`truncate min-w-0 ${muted}`}>{a.subject_name} · {a.school_name}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // No celular tudo empilha. No desktop, com `ladoDesktop`: a lista de hoje à
  // esquerda e `ladoDesktop` à direita. Sem ele: Agora/Próxima/amanhã à
  // esquerda e a linha do tempo à direita — esticada na largura toda, cada
  // aula virava uma faixa de 1.400 px com o texto num canto.
  const colunas = ladoDesktop
    ? 'lg:grid-cols-2'
    : 'lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:items-start';
  return (
    <section className={`mb-6 grid gap-4 ${colunas}`}>
      {/* Com `ladoDesktop`, o desktop troca esta coluna pela lista de hoje. */}
      <div className={`flex flex-col gap-3 min-w-0 ${ladoDesktop ? 'lg:hidden' : ''}`}>
        {titulo}
        {/* Agora / Próxima */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
          <CartaoAula
            rotulo="Agora"
            aula={atual}
            destaque
            darkMode={darkMode}
            onAbrir={(a) => onAbrirAula?.(a, agora)}
            progresso={atual ? Math.round(((minAgora - atual.ini) / (atual.fim - atual.ini)) * 100) : null}
            detalhe={atual ? `Termina em ${duracao(atual.fim - minAgora)}` : 'Sem aula neste horário'}
          />
          <CartaoAula
            rotulo="Próxima"
            aula={proxima}
            darkMode={darkMode}
            onAbrir={(a) => onAbrirAula?.(a, proxima.data)}
            detalhe={quandoProxima()}
          />
        </div>
        {!ladoDesktop && listaAmanha(`hidden lg:block rounded-2xl ${card}`)}
      </div>
      {ladoDesktop && listaHoje}

      {ladoDesktop && <div className="hidden lg:flex lg:flex-col min-w-0">{ladoDesktop}</div>}

      <button
        type="button"
        onClick={() => setDiaAberto((v) => !v)}
        aria-expanded={diaAberto}
        className={`lg:hidden flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm font-medium ${card} ${strong}`}
      >
        <span>
          {diaAberto ? 'Esconder o dia todo' : 'Ver o dia todo'}
          <span className={`ml-2 font-normal ${muted}`}>
            · {aulasDeHoje} {aulasDeHoje === 1 ? 'aula' : 'aulas'} hoje
          </span>
        </span>
        <span className={`text-lg leading-none transition-transform ${diaAberto ? 'rotate-90' : ''} ${muted}`} aria-hidden="true">›</span>
      </button>

      <div className={`rounded-2xl overflow-hidden min-w-0 ${diaAberto ? '' : 'hidden'} ${ladoDesktop ? 'lg:hidden' : 'lg:block'} ${card}`}>
        {/* Fita da semana */}
        <div className={`px-2 pt-3 pb-2 border-b ${linha}`}>
          <div className="grid grid-cols-7 text-center">
            {semana.map((d, i) => {
              const sel = mesmoDia(d, diaSel);
              const hoje = mesmoDia(d, agora);
              const temAula = aulasDoDia(slots, d).length > 0;
              return (
                <button key={i} type="button" onClick={() => setDiaSel(d)} className="flex flex-col items-center gap-1 py-1">
                  <span className={`text-[11px] font-semibold ${muted}`}>{DIAS_CURTOS[i]}</span>
                  <span
                    className={`w-9 h-9 grid place-items-center rounded-full text-lg tabular-nums transition-colors ${
                      sel ? 'text-white font-semibold' : hoje ? 'font-semibold' : temAula ? strong : muted
                    }`}
                    style={sel ? { background: hoje ? AGORA_COR : '#2E4974' } : hoje ? { color: AGORA_COR } : undefined}
                  >
                    {d.getDate()}
                  </span>
                  <span className="h-1 w-1 rounded-full" style={{ background: temAula && !sel ? (darkMode ? '#64748B' : '#CBD5E1') : 'transparent' }} />
                </button>
              );
            })}
          </div>
        </div>
        <div className={`py-2.5 text-center text-sm font-semibold border-b ${linha} ${strong}`}>
          {DIAS_LONGOS[diaSel.getDay()]} – {diaSel.getDate()} de {MESES[diaSel.getMonth()]} de {diaSel.getFullYear()}
          <span className={`ml-2 font-normal ${muted}`}>
            · {aulas.length ? `${aulas.length} ${aulas.length === 1 ? 'aula' : 'aulas'}` : 'sem aulas'}
          </span>
        </div>

        {/* Linha do tempo */}
        <div ref={rolagem} className="relative overflow-y-auto" style={{ maxHeight: 520 }}>
          <div className="relative" style={{ height: (horaFim - horaIni) * HORA_PX + 24 }}>
            {horas.map((h) => (
              <div key={h} className="absolute left-0 right-0 flex items-center" style={{ top: topo(h * 60) + 12 }}>
                {/* Como no celular: a hora some quando o traço de agora passa por cima. */}
                <span className={`w-14 pr-2 -translate-y-1/2 text-right text-xs tabular-nums ${muted} ${
                  ehHoje && Math.abs(minAgora - h * 60) < 15 ? 'invisible' : ''}`}>{hhmm(h * 60)}</span>
                <div className={`flex-1 border-t ${linha}`} />
              </div>
            ))}

            {comHorario.map((a) => {
              const cor = corDaDisciplina(a.id_subject);
              const passou = ehHoje && a.fim <= minAgora;
              const emCurso = ehHoje && a.ini <= minAgora && minAgora < a.fim;
              const alto = (a.fim - a.ini) / 60 * HORA_PX;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onAbrirAula?.(a, diaSel)}
                  className="absolute left-16 right-3 rounded-lg px-3 py-1.5 text-left overflow-hidden transition-opacity hover:opacity-90"
                  style={{
                    top: topo(a.ini) + 13,
                    height: Math.max(alto - 2, 22),
                    // Cor translúcida sobre fundo opaco: as linhas das horas não atravessam a aula.
                    backgroundColor: darkMode ? '#1E293B' : '#FFFFFF',
                    backgroundImage: `linear-gradient(${cor}${darkMode ? '33' : '1F'}, ${cor}${darkMode ? '33' : '1F'})`,
                    borderLeft: `4px solid ${cor}`,
                    opacity: passou ? 0.5 : 1,
                    boxShadow: emCurso ? `0 0 0 1.5px ${cor}` : undefined,
                  }}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={`text-sm font-semibold truncate ${strong}`}>{a.class_name} · {a.subject_name}</span>
                    <span className={`text-[11px] tabular-nums flex-none ${muted}`}>{a.period}ª aula</span>
                  </div>
                  {alto > 40 && (
                    <div className={`text-xs truncate ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      {hhmm(a.ini)} – {hhmm(a.fim)} · {a.school_name}
                    </div>
                  )}
                </button>
              );
            })}

            {ehHoje && minAgora >= horaIni * 60 && minAgora <= horaFim * 60 && (
              <div className="absolute left-0 right-0 flex items-center pointer-events-none z-10" style={{ top: topo(minAgora) + 12 }}>
                <span
                  className="ml-1 -translate-y-1/2 px-1.5 py-0.5 rounded-full text-[11px] font-semibold text-white tabular-nums"
                  style={{ background: AGORA_COR }}
                >
                  {hhmm(minAgora)}
                </span>
                <div className="flex-1 -translate-y-1/2 h-0.5" style={{ background: AGORA_COR }} />
              </div>
            )}
          </div>
        </div>

        {/* No celular o dia seguinte fecha o cartão; no desktop ele vai para a coluna da esquerda. */}
        {listaAmanha(`lg:hidden border-t ${linha}`)}

        {semHorario.length > 0 && (
          <div className={`px-4 py-3 border-t text-xs ${linha} ${muted}`}>
            Sem horário cadastrado na grade:{' '}
            {semHorario.map((a) => `${a.period}ª aula · ${a.class_name} (${a.subject_name})`).join(' — ')}
          </div>
        )}
      </div>
    </section>
  );
}
